/**
 * @module cercaliagl/layer/Feature
 */
import LonLat from '../LonLat';
import BaseLayer, { LayerType } from './Base';
import { binarySearchZIndex } from '../util.js';
import { IMG } from '../globals';
/**
 * @typedef {Object} Options
 * @property {string} id Layer id
 * @property {import("../Map").default} map Mapbox Map
 * @property {string} [beforeId] Before layer id
 * @property {string} type Type
 * @property {string} srcArrow src arrow (arrow must be pointing to the right)
 */

/**
 * @typedef {Object} InternalFeatureLayerIds
 * @property {string} source Source
 * @property {string} line Line
 * @property {string} lineOutline Outline
 * @property {string} fill Fill
 * @property {string} fillStroke Fill stroke
 * @property {string} fillOutline Fill outline
 * @property {string} fillExtrusion Fill extrussion
 * @property {string} text Text
 */

/**
 * @constant
 * @type {Object.<string, Object>}
 */
const imagesLoaded = {};

/**
 * @classdesc
 */
class Feature extends BaseLayer {
  /**
   * @constructor
   * @param {Options} options Feature layer optiosn
   */
  constructor(options) {
    super({
      id: options.id,
      map: options.map,
      beforeId: options.beforeId,
      type: LayerType.FEATURE,
    });

    /**
     * @private
     * @type {Object.<string, import("../Feature").default>}
     */
    this.features_ = {};

    /**
     * @protected
     * @type {Array.<import("../Feature     * @private
/
    this.featuresMouseOvered = [];

    /**
     * @private
     * @type {string}
     */
    this.srcArrow_ = options.srcArrow;

    /**
     * @private
     * @type {number}
     */
    this.layerOpacityFactor_ = options.layerOpacityFactor !== undefined ? options.layerOpacityFactor : 1;

    /**
     * @private
     * @type {InternalFeatureLayerIds}
     */
    this.layerIds_ = /** @type {InternalFeatureLayerIds} */ ({
      source: `${this.id_}`,
      line: `${this.id_}_line`,
      lineOutline: `${this.id_}_line_outline`,
      fill: `${this.id_}_fill`,
      fillOutline: `${this.id_}_fill_outline`,
      fillStroke: `${this.id_}_fill_stroke_outline`,
      fillExtrusion: `${this.id_}_fill_extrusion`,
      text: `${this.id_}_text`,
      direction: `${this.id_}line_direction`,
    });
  }

  /**
   * @api
   * @return {number} Layer opacity factor
  */
  getLayerOpacityFactor() {
    return this.layerOpacityFactor_;
  }

  /**
   * @api
   * @param {number} layerOpacityFactor Layer opacity factor
  */
  setLayerOpacityFactor(layerOpacityFactor) {
    this.layerOpacityFactor_ = layerOpacityFactor;
    this.refreshGeoJSON();
    this.map_.setPaintProperty(this.layerIds_.line, 'line-opacity', ['*', ['get', 'strokeOpacity'], this.layerOpacityFactor_]);
    this.map_.setPaintProperty(this.layerIds_.fill, 'fill-opacity', ['*', ['get', 'fillOpacity'], this.layerOpacityFactor_]);
    this.map_.setPaintProperty(this.layerIds_.fillExtrusion, 'fill-extrusion-opacity', this.layerOpacityFactor_);
    this.map_.setPaintProperty(this.layerIds_.lineOutline, 'line-opacity', ['*', ['get', 'outlineOpacity'], this.layerOpacityFactor_]);
    this.map_.setPaintProperty(this.layerIds_.fillStroke, 'line-opacity', ['*', ['get', 'strokeOpacity'], this.layerOpacityFactor_]);
    this.map_.setPaintProperty(this.layerIds_.fillOutline, 'line-opacity', ['*', ['get', 'strokeOpacity'], this.layerOpacityFactor_]);
    this.map_.setPaintProperty(this.layerIds_.text, 'text-opacity', ['*', ['get', 'textOpacity'], this.layerOpacityFactor_]);
  }

  /**
   */
  initialize() {
    this.handleClick_ = this.handleClick_.bind(this);
    this.handleDoubleClick_ = this.handleDoubleClick_.bind(this);
    this.handleMouseEnter_ = this.handleMouseEnter_.bind(this);
    this.handleMouseMove_ = this.handleMouseMove_.bind(this);
    this.handleMouseLeave_ = this.handleMouseLeave_.bind(this);

    this.initializeLayers_();
  }

  /**
   * @private
   */
  initializeLayers_() {
    this.map_.addSource(this.layerIds_.source, {
      type: 'geojson',
      data: this.geojson_,
    });

    // Outline layer
    this.addLayer({
      id: this.layerIds_.lineOutline,
      source: this.layerIds_.source,
      type: 'line',
      filter: [
        'all',
        ['==', '$type', 'LineString'],
        ['==', 'visible', true],
        ['==', 'outline', true],
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['get', 'outlineColor'],
        'line-width': ['get', 'outlineWidth'],
        'line-opacity': ['*', ['get', 'outlineOpacity'], this.layerOpacityFactor_],

        // 'line-opacity': ['get', 'outlineOpacity'],
      },
    });

    this.addLayer({
      id: this.layerIds_.line,
      source: this.layerIds_.source,
      type: 'line',
      filter: [
        'all',
        ['==', '$type', 'LineString'],
        ['==', 'visible', true],
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['get', 'strokeColor'],
        'line-width': ['get', 'strokeWidth'],
        'line-opacity': ['*', ['get', 'strokeOpacity'], this.layerOpacityFactor_],
        // 'line-opacity': ['get', 'strokeOpacity'],
      },
    });

    if (!imagesLoaded['cercalia-arrow']) {
      imagesLoaded['cercalia-arrow'] = true;
      const src = this.srcArrow_ || `${IMG}/cercalia-arrow.png`;
      this.map_.loadImage(src, (error, image) => {
        this.initializeArrowsLayer(error, image);
      });
    } else {
      this.addArrowsLayer();
    }

    this.addLayer({
      id: this.layerIds_.fill,
      source: this.layerIds_.source,
      type: 'fill',
      filter: [
        'all',
        ['==', '$type', 'Polygon'],
        ['==', 'height', 0],
        ['==', 'visible', true],
      ],
      layout: {},
      paint: {
        'fill-color': ['get', 'fillColor'],
        // 'fill-opacity': ['get', 'fillOpacity'],
        'fill-opacity': ['*', ['get', 'fillOpacity'], this.layerOpacityFactor_],
      },
    });

    // Outline layer
    this.addLayer({
      id: this.layerIds_.fillStroke,
      source: this.layerIds_.source,
      type: 'line',
      filter: [
        'all',
        ['==', '$type', 'Polygon'],
        ['==', 'visible', true],
        ['==', 'outline', true],
      ],
      layout: {
        'line-join': 'round',
        'line-cap': 'round',
      },
      paint: {
        'line-color': ['get', 'outlineColor'],
        'line-width': ['get', 'outlineWidth'],
        // 'line-opacity': ['get', 'outlineOpacity'],
        'line-opacity': ['*', ['get', 'outlineOpacity'], this.layerOpacityFactor_],
      },
    });

    this.addLayer({
      id: this.layerIds_.fillOutline,
      source: this.layerIds_.source,
      type: 'line',
      filter: [
        'all',
        ['==', '$type', 'Polygon'],
        ['==', 'height', 0],
        ['==', 'visible', true],
      ],
      layout: {},
      paint: {
        'line-color': ['get', 'strokeColor'],
        'line-width': ['get', 'strokeWidth'],
        // 'line-opacity': ['get', 'strokeOpacity'],
        'line-opacity': ['*', ['get', 'strokeOpacity'], this.layerOpacityFactor_],
      },
    });

    this.addLayer({
      id: this.layerIds_.fillExtrusion,
      source: this.layerIds_.source,
      type: 'fill-extrusion',
      filter: ['all',
        ['==', '$type', 'Polygon'],
        ['>', 'height', 0],
        ['==', 'visible', true],
      ],
      paint: {
        'fill-extrusion-color': ['get', 'fillColor'],
        'fill-extrusion-opacity': 0.8,
        'fill-extrusion-height': ['interpolate', ['linear'], ['zoom'], 15, 0, 15.05, ['get', 'height']],
      },
    });

    this.addLayer({
      id: this.layerIds_.text,
      source: this.layerIds_.source,
      type: 'symbol',
      filter: ['all',
        ['has', 'textField'],
        ['==', 'visible', true],
      ],
      layout: {
        'text-font': ['Open Sans Regular'],
        'text-anchor': ['get', 'textAnchor'],
        'text-offset': ['get', 'textOffset'],
        'text-size': ['get', 'textSize'],
        'text-field': ['get', 'textField'],
        'text-justify': ['get', 'textJustify'],
        'text-allow-overlap': true,
        'text-ignore-placement': true,
        'text-rotate': ['get', 'textRotation'],
      },
      paint: {
        'text-halo-color': ['get', 'textStrokeColor'],
        'text-halo-width': ['get', 'textStrokeWidth'],
        'text-color': ['get', 'textColor'],
        'text-opacity': ['*', ['get', 'textOpacity'], this.layerOpacityFactor_],
      },
    });

    this.initializeEventsListeners_();
    this.source_ = this.map_.getSource(this.layerIds_.source);
  }

  /**
   * @private
  */
  initializeArrowsLayer(error, image) {
    if (!error && image) {
      this.map_.addImage('cercalia-arrow', image);
      this.addArrowsLayer();
    }
  }

  /**
   * @private
  */
  addArrowsLayer() {
    this.addLayer({
      id: this.layerIds_.direction,
      type: 'symbol',
      source: this.layerIds_.source,
      layout: {
        'symbol-placement': 'line',
        'icon-rotation-alignment': 'map',
        'icon-image': 'cercalia-arrow',
        'icon-size': ['interpolate', ['exponential', 1.5], ['zoom'], 12, 0.85, 18, 1.4],
      },
      filter: [
        'all',
        ['==', 'showDirection', true],
      ],
    });
  }

  /**
   * Remove internal layers
   * @override
   */
  removeLayers() {
    this.removeEventsListeners_();
    super.removeLayers();
  }

  /**
   * Get first internal layer id. For `beforeId` uses propose
   * @return {string}
   */
  getFirstInternalLayerId() {
    return this.layerIds_.lineOutline;
  }

  /**
   * @private
   */
  initializeEventsListeners_() {
    const activeLayersId = [this.layerIds_.line, this.layerIds_.fill, this.layerIds_.fillExtrusion];
    activeLayersId.forEach((layerId) => {
      this.map_.on('click', layerId, this.handleClick_);
      this.map_.on('dblclick', layerId, this.handleDoubleClick_);
      this.map_.on('mouseenter', layerId, this.handleMouseEnter_);
      this.map_.on('mousemove', layerId, this.handleMouseMove_);
      this.map_.on('mouseleave', layerId, this.handleMouseLeave_);
    });
  }

  /**
   * @private
   */
  removeEventsListeners_() {
    const activeLayersId = [this.layerIds_.line, this.layerIds_.fill, this.layerIds_.fillExtrusion];
    activeLayersId.forEach((layerId) => {
      this.map_.off('click', layerId, this.handleClick_);
      this.map_.off('dblclick', layerId, this.handleDoubleClick_);
      this.map_.off('mouseenter', layerId, this.handleMouseEnter_);
      this.map_.off('mousemove', layerId, this.handleMouseMove_);
      this.map_.off('mouseleave', layerId, this.handleMouseLeave_);
    });
  }

  /**
   * @private
   * @param {Event} e Event
   * @return {Array.<import("../Feature").default>} Feature
   */
  getFeaturesFromEvent_(e) {
    return e.features.map((feature) => this.features_[feature.properties.id]);
  }

  /**
   * @private
   * @param {Event} e Event
   */
  handleClick_(e) {
    e.preventDefault();
    const cercaliaFeatures = this.getFeaturesFromEvent_(e);
    if (this.cercaliaMap_.getMultipleFeaturesInPixel()) {
      cercaliaFeatures.forEach((cercaliaFeature) => {
        if (cercaliaFeature.onClick) {
          cercaliaFeature.onClick(cercaliaFeature, LonLat.fromMapboxgl(e.lngLat), e);
        }
        if (cercaliaFeature.isEditable()) {
          cercaliaFeature.editMode(true);
        }
      });
    } else {
      const featuresAtPoint = this.map_.queryRenderedFeatures(e.point, { layers: this.cercaliaMap_.getLayerManager().getFeaturesLayersIds() });
      const featureWithMaxZIndex = featuresAtPoint.reduce((prev, current) => (prev.properties.zIndex >= current.properties.zIndex ? prev : current));
      if (featureWithMaxZIndex.layer.type === 'symbol') {
        // There's a marker above the feature
        return;
      }
      const cercaliaFeatureWithMaxZIndex = cercaliaFeatures.find((el) => el.getId() === featureWithMaxZIndex.properties.id);
      if (cercaliaFeatureWithMaxZIndex?.onClick) {
        cercaliaFeatureWithMaxZIndex.onClick(cercaliaFeatureWithMaxZIndex, LonLat.fromMapboxgl(e.lngLat), e);
      }
      if (cercaliaFeatureWithMaxZIndex?.isEditable()) {
        cercaliaFeatureWithMaxZIndex.editMode(true);
      }
    }
  }

  /**
   * @private
   * @param {Event} e Event
   */
  handleDoubleClick_(e) {
    e.preventDefault();
    const cercaliaFeatures = this.getFeaturesFromEvent_(e);
    cercaliaFeatures.forEach((cercaliaFeature) => {
      if (cercaliaFeature.onDoubleClick) {
        cercaliaFeature.onDoubleClick(cercaliaFeature, LonLat.fromMapboxgl(e.lngLat), e);
      }
    });
  }

  /**
   * @private
   * @param {Event} e Event
   */
  handleMouseMove_(e) {
    e.preventDefault();
    const cercaliaFeatures = this.getFeaturesFromEvent_(e);
    cercaliaFeatures.forEach((cercaliaFeature) => {
      if (this.featuresMouseOvered.indexOf(cercaliaFeature) === -1) {
        this.featuresMouseOvered.push(cercaliaFeature);
        this.clusterMouseOvered_ = null;
        if (cercaliaFeature.onMouseOver) {
          cercaliaFeature.onMouseOver(cercaliaFeature, LonLat.fromMapboxgl(e.lngLat), e);
        }
      } else if (cercaliaFeature.onMouseMove) {
        cercaliaFeature.onMouseMove(cercaliaFeature, LonLat.fromMapboxgl(e.lngLat), e);
      }
    });

    // Detect mouseOut markers
    for (let i = this.featuresMouseOvered.length - 1; i >= 0; i -= 1) {
      const cercaliaFeature = this.featuresMouseOvered[i];
      if (cercaliaFeature !== null && cercaliaFeature !== undefined) {
        if (cercaliaFeatures.indexOf(cercaliaFeature) === -1) {
          if (cercaliaFeature.onMouseOut) {
            cercaliaFeature.onMouseOut(cercaliaFeature, LonLat.fromMapboxgl(e.lngLat), e);
          }
          this.featuresMouseOvered.splice(i, 1);
        }
      }
    }
  }

  /**
   * @private
   */
  handleMouseEnter_() {
    this.canvas_.style.cursor = 'pointer';
  }

  /**
   * @private
   * @param {Event} e Event
   */
  handleMouseLeave_(e) {
    e.preventDefault();
    this.canvas_.style.cursor = '';
    if (this.featuresMouseOvered.length > 0) {
      this.featuresMouseOvered.forEach((marker) => {
        if (marker && marker.onMouseOut) {
          marker.onMouseOut(marker, LonLat.fromMapboxgl(e.lngLat), e);
        }
      });
    }
    this.featuresMouseOvered = [];
  }

  /**
   * Obtain all features
   * @return {Array.<import("../Feature").default>} Array with features. Features {@link module:cercaliagl/Feature~Feature}.
   */
  getFeatures() {
    return Object.entries(this.features_).map((entry) => entry[1]);
  }

  /**
   * Obtain feature by id.
   * @param {string} id Id.
   * @return {import("../Feature").default} Feature requested.
   */
  getFeatureById(id) {
    return this.features_[id];
  }

  /**
   * Add feature to layer.
   * @param {import("../Feature").default} feature Feature
   * @param {boolean} updateGeoJSON Update GeoJSON after add feature
   */
  addFeature(feature, updateGeoJSON = true) {
    const id = feature.getId();
    if (this.features_[id] === undefined) {
      this.features_[id] = feature;
      feature.setLayer(this);
      const geojson = feature.getGeoJSON();
      const geojsonFeaturesLength = this.geojson_.features.length;
      if (geojsonFeaturesLength > 0) {
        const positionToInsert = binarySearchZIndex(this.geojson_.features, feature.getZIndex(), 0, geojsonFeaturesLength - 1);
        this.geojson_.features.splice(positionToInsert, 0, geojson);
      } else {
        this.geojson_.features.push(geojson);
      }
      if (updateGeoJSON) {
        this.updateGeoJSON();
      }
    } else {
      throw new Error(`Feature with id=${id} already exists`);
    }
  }

  /**
   * Add features to layer.
   * @param {Array.<import("./Feature").default>} features Features
   * @param {import('../Map').default} map Map
   */
  addFeatures(features, map) {
    features.forEach((feature) => {
      this.addFeature(feature, false);
      feature.setMap(map);
    });
    this.refreshGeoJSON();
  }

  /**
   * @param {import("../Feature").default} feature Feature
   * @param {Function} [callbackFn] Callback function
   */
  addAnimatedFeature(feature, callbackFn) {
    let i = 0;
    const id = feature.getId();
    const geometry = feature.getGeometry();
    if (this.features_[id] === undefined) {
      this.features_[id] = feature;
      const { coordinates } = geometry;
      feature.setLayer(this);
      const geojson = feature.getGeoJSON();
      geojson.geometry.coordinates = [];
      this.geojson_.features.push(geojson);
      const timer = window.setInterval(() => {
        if (i < coordinates.length) {
          geojson.geometry.coordinates.push(coordinates[i]);
          this.updateGeoJSON();
          i += 1;
        } else {
          window.clearInterval(timer);
          callbackFn();
        }
      }, 10);
    }
  }

  /**
   * @param {import("../Feature").default} feature Feature
   */
  removeFeature(feature) {
    this.removeFeatureInner_(feature);
    this.refreshGeoJSON();
  }

  /**
   * @param {Array.<import("../Feature").default>} features Features
   */
  removeFeatures(features) {
    features.forEach((feature) => this.removeFeatureInner_(feature));
    this.refreshGeoJSON();
  }

  /**
   */
  removeAllFeatures() {
    Object.entries(this.features_).forEach(([, feature]) => {
      if (feature) {
        this.removeFeatureInner_(feature);
      }
    });
    this.features_ = {};
    this.refreshGeoJSON();
  }

  /**
   * @private
   * @param {import("../Feature").default} feature Feature
   */
  removeFeatureInner_(feature) {
    const id = feature.getId();
    feature.setMap(null);
    feature.setLayer(null);
    this.removeFeatureGeoJSON(id);
    delete this.features_[id];
  }

  /**
   * Update GeoJSON
   */
  updateGeoJSON() {
    this.source_.setData(this.geojson_);
  }

  /**
   * @return {JSON} JSON Object.
   */
  getGeoJSON() {
    return this.geojson_;
  }

  /**
   * @api
   */
  clear() {
    this.removeAllFeatures();
  }
}

export default Feature;
